import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { useAttendanceUpdates, PartnershipConflict, AttendanceUpdate, PanelSwitchUpdate, ToastDismissUpdate } from '../hooks/useAttendanceUpdates';
import { useAuth } from '../hooks/useAuth';
import LernbueroTasks from '../components/LernbueroTasks';
import StudentInfoModal from '../components/StudentInfoModal';
import EmailModal from '../components/EmailModal';
import MemoModal from '../components/MemoModal';
import EmailCredentialModal from '../components/EmailCredentialModal';
import PersonalDataRequiredModal from '../components/PersonalDataRequiredModal';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';
import { 
  Plus, 
  Calendar, 
  Users, 
  BookOpen,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserPlus,
  UserMinus,
  Search,
  Info,
  X,
  StickyNote,
  Mail,
  Check,
  Timer,
  ThumbsUp,
  ThumbsDown,
  CheckSquare,
  Tv
} from 'lucide-react';
import toast from 'react-hot-toast';
import { createAttendanceToast, playTimerExpirationSound } from '../utils/memoToast';
import { connectDisplay, disconnectDisplay, fetchMyDisplayConnection, teacherUpdateLernbueroDashboard } from '../lib/displayApi';
import AbsenceToggle from '../components/AbsenceToggle';
import { fetchActiveAbsences } from '../lib/temporaryAbsencesApi';
import {
  Badge,
  Button,
  Modal,
  SortableTh,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../components/ui';

interface Session {
  SessionID: number;
  Lehrer: string;
  Fach: string;
  TimeStamp: string;
  studentCount: number;
  evidenceCount: number;
}

interface AttendanceData {
  Name: string;
  Vorname: string;
  Klasse: string;
  Anzahl: number;
  /** Belege zu Aufgaben aktuell aktiver Lernpläne (D/M/E, wie Fortschritt in /Statistik) */
  BelegeAbschnitt?: number;
  Unid?: string;
  ID?: string | number;
  sophomorixUnid?: string;
  Memo?: string;
  Foto?: string;
}

interface AvailableStudent {
  Unid: string;
  Name: string;
  Vorname: string;
  Klasse: string;
}

/** Sortierbare Spalten der Anwesenheitstabelle (Panel A) */
type LernbueroPanelASortField =
  | 'Auswahl'
  | 'Foto'
  | 'Name'
  | 'Vorname'
  | 'Klasse'
  | 'BelegeAbschnitt'
  | 'Anzahl'
  | 'Aktionen'

const LERNBUERO_PANEL_A_SORT_FIELDS = [
  'Auswahl',
  'Foto',
  'Name',
  'Vorname',
  'Klasse',
  'BelegeAbschnitt',
  'Anzahl',
  'Aktionen',
] as const satisfies readonly LernbueroPanelASortField[]

function parseLernbueroPanelASortField(raw: string | null): LernbueroPanelASortField {
  if (raw && (LERNBUERO_PANEL_A_SORT_FIELDS as readonly string[]).includes(raw)) {
    return raw as LernbueroPanelASortField
  }
  return 'Name'
}

interface NewSessionForm {
  Lehrer: string;
  Fach: string;
}

const BAUSTEIN_SUBJECTS = [
  'Deutsch',
  'Mathematik',
  'Englisch',
  'Französisch',
  'Biologie',
  'Chemie',
  'Physik',
  'Geschichte',
  'Erdkunde',
  'Politik',
  'Religion',
  'Kunst',
  'Musik',
  'Sport'
] as const
type BausteinSubject = (typeof BAUSTEIN_SUBJECTS)[number]
const FAVORITE_SUBJECTS: BausteinSubject[] = ['Deutsch', 'Mathematik', 'Englisch']
const NEW_SESSION_SUBJECTS = [
  ...FAVORITE_SUBJECTS,
  ...BAUSTEIN_SUBJECTS.filter((subject) => !FAVORITE_SUBJECTS.includes(subject))
]

export default function Lernbuero() {
  // Initialize state from sessionStorage or defaults
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('lernbuero-selected-session-id')
    return saved ? parseInt(saved) : null
  })
  
  const [showNewSessionModal, setShowNewSessionModal] = useState(false)
  
  const [newSessionForm, setNewSessionForm] = useState<NewSessionForm>(() => {
    const saved = sessionStorage.getItem('lernbuero-new-session-form')
    return saved ? JSON.parse(saved) : { Lehrer: '', Fach: '' }
  })
  
  const [panelType, setPanelType] = useState<'A' | 'B'>(() => {
    const saved = sessionStorage.getItem('lernbuero-panel-type')
    return (saved as 'A' | 'B') || 'A'
  })
  
  const [sessionPanelCollapsed, setSessionPanelCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('lernbuero-session-panel-collapsed')
    return saved ? JSON.parse(saved) : false
  })
  
  const [sortField, setSortField] = useState<LernbueroPanelASortField>(() => {
    const saved = sessionStorage.getItem('lernbuero-sort-field')
    return parseLernbueroPanelASortField(saved)
  })
  
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const saved = sessionStorage.getItem('lernbuero-sort-direction')
    return (saved as 'asc' | 'desc') || 'asc'
  })
  
  // Schülerfotos
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({})
  
  const [showAddStudentModal, setShowAddStudentModal] = useState(false)
  const [availableStudents, setAvailableStudents] = useState<AvailableStudent[]>([])
  const [selectedStudentsInModal, setSelectedStudentsInModal] = useState<string[]>([])
  
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = sessionStorage.getItem('lernbuero-search-term')
    return saved || ''
  })

  // Panel B State
  const [selectedStudentForPanelB, setSelectedStudentForPanelB] = useState<AttendanceData | null>(() => {
    const saved = sessionStorage.getItem('lernbuero-selected-student-panel-b')
    return saved ? JSON.parse(saved) : null
  })

  // State für Student Info Modal
  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false)
  const [selectedStudentForInfo, setSelectedStudentForInfo] = useState<AttendanceData | null>(null)
  
  const [showMemoModal, setShowMemoModal] = useState(false)
  const [selectedStudentForMemo, setSelectedStudentForMemo] = useState<AttendanceData | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailRecipient, setEmailRecipient] = useState<string | undefined>(undefined)
  const [emailStudentName, setEmailStudentName] = useState<string>('')
  const [emailInitialSubject, setEmailInitialSubject] = useState<string>('')
  const [emailInitialBody, setEmailInitialBody] = useState<string>('')
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false)
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false)
  const [pendingEmailStudent, setPendingEmailStudent] = useState<AttendanceData | null>(null)
  // Multi-select state for group actions
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([])
  const [showGroupTimerModal, setShowGroupTimerModal] = useState(false)
  const [groupTimerMinutes, setGroupTimerMinutes] = useState<number>(15)
  const [showGroupsModal, setShowGroupsModal] = useState(false)
  // Group work warning modal (no time limit)
  const [showGroupWorkWarning, setShowGroupWorkWarning] = useState(false)
  const [groupWorkConflicts, setGroupWorkConflicts] = useState<PartnershipConflict[]>([])
  const [groupWorkConflictNameMap, setGroupWorkConflictNameMap] = useState<Record<string, string>>({})
  
  // Individual student addition warning modal (6 second timeout)
  const [showStudentAdditionWarning, setShowStudentAdditionWarning] = useState(false)
  const [studentAdditionConflicts, setStudentAdditionConflicts] = useState<PartnershipConflict[]>([])
  const [studentAdditionConflictNameMap, setStudentAdditionConflictNameMap] = useState<Record<string, string>>({})
  const [pendingStudentData, setPendingStudentData] = useState<{unid: string, name: string, vorname: string, klasse: string} | null>(null)
  const studentWarningTimeoutRef = useRef<number | null>(null)
  // Multiple groups tied to the current Session (server-persisted)
  type Group = { id: number; color_class: string; memberIds: string[]; deadline: string; deadline_ms?: number };
  const [groups, setGroups] = useState<Group[]>([])
  const [displayPin, setDisplayPin] = useState(() => sessionStorage.getItem('lernbuero-display-pin') || '')
  const [displayConnectedPin, setDisplayConnectedPin] = useState<string | null>(null)
  const [displayConnectBusy, setDisplayConnectBusy] = useState(false)
  const [displayDashboardEnabled, setDisplayDashboardEnabled] = useState<boolean>(() => {
    const saved = sessionStorage.getItem('lernbuero-display-dashboard-enabled');
    return saved ? JSON.parse(saved) : true;
  })
  const overdueNotifiedRef = useRef<Set<number>>(new Set())
  const [, setTick] = useState(0)

  // Helper function to calculate remaining time (counts up past expiry)
  const getRemainingTime = (deadlineMs: number) => {
    const now = Date.now();
    const remaining = deadlineMs - now;
    
    if (remaining <= 0) {
      const elapsed = Math.abs(remaining);
      const minutes = Math.floor(elapsed / (1000 * 60));
      const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);
      const text = minutes > 0 ? `+${minutes}m ${seconds}s` : `+${seconds}s`;
      return { text, color: 'text-red-600 dark:text-red-400', overdue: true };
    }
    
    const minutes = Math.floor(remaining / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    if (minutes > 0) {
      return {
        text: `${minutes}m ${seconds}s`,
        color: minutes < 5 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400',
        overdue: false
      };
    } else {
      return { text: `${seconds}s`, color: 'text-red-600 dark:text-red-400', overdue: false };
    }
  };

  const queryClient = useQueryClient();
  const { user } = useAuth();

  const parseAbsenceStartedAtMs = (value: string) => {
    const sqlLike = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;
    const match = String(value || '').match(sqlLike);
    if (match) {
      const [, y, mo, d, h, mi, s] = match;
      const asLocalMs = new Date(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(h),
        Number(mi),
        Number(s)
      ).getTime();
      const asUtcMs = Date.UTC(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(h),
        Number(mi),
        Number(s)
      );
      const now = Date.now();
      const localDiff = Math.abs(now - asLocalMs);
      const utcDiff = Math.abs(now - asUtcMs);
      return localDiff <= utcDiff ? asLocalMs : asUtcMs;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Helper function to get student ID safely
  const getStudentId = (student: AttendanceData | AvailableStudent): string => {
    if ('sophomorixUnid' in student && student.sophomorixUnid) return student.sophomorixUnid;
    if ('Unid' in student && student.Unid) return student.Unid;
    if ('ID' in student && student.ID) return String(student.ID);
    return '';
  };

  // Fotos für Schüler laden (in Batches für bessere Performance)
  const loadAllStudentPhotos = async (students: (AttendanceData | AvailableStudent)[]) => {
    try {
      const sophomorixUnids = students
        .map(s => {
          if ('sophomorixUnid' in s) return s.sophomorixUnid;
          if ('Unid' in s) return s.Unid;
          if ('ID' in s) return String(s.ID);
          return null;
        })
        .filter((id): id is string => Boolean(id));
      
      if (sophomorixUnids.length === 0) return;
      
      console.log(`📸 Lade Fotos für ${sophomorixUnids.length} Schüler im Lernbüro...`);
      
      // Fotos in Batches von 20 laden für bessere Performance
      const batchSize = 20;
      const batches = [];
      for (let i = 0; i < sophomorixUnids.length; i += batchSize) {
        batches.push(sophomorixUnids.slice(i, i + batchSize));
      }
      
      const allPhotos: Record<string, string | null> = {};
      
      for (const batch of batches) {
        try {
          const photos = await loadStudentPhotos(batch);
          Object.assign(allPhotos, photos);
          
          // Kurze Pause zwischen Batches um Server nicht zu überlasten
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.warn('Fehler beim Laden eines Foto-Batches:', error);
          // Setze null für alle IDs in diesem Batch
          batch.forEach(id => {
            if (id) allPhotos[id] = null;
          });
        }
      }
      
      console.log(`✅ ${Object.values(allPhotos).filter(p => p !== null).length} Fotos geladen`);
      setStudentPhotos(allPhotos);
    } catch (error) {
      console.warn('Fehler beim Laden der Schülerfotos:', error);
    }
  };

  // Function to send toast dismiss event to other users
  const sendToastDismissEvent = async (groupId: number, action: string) => {
    if (!selectedSessionId) return;
    
    try {
      await api.post(`/lernbuero/sessions/${selectedSessionId}/toast-dismiss`, {
        groupId,
        action
      });
      console.log(`🍞 Toast dismiss event sent for group ${groupId}, action: ${action}`);
    } catch (error) {
      console.error('❌ Error sending toast dismiss event:', error);
    }
  };

  // Get current user for active status tracking
  const { data: userResponse } = useQuery(
    ['auth-me'],
    () => api.get('/auth/me').then(res => res.data)
  );

  const currentUser = userResponse?.data?.username || user?.username;

  // Track active status when component mounts/unmounts and when session changes
  useEffect(() => {
    if (currentUser && selectedSessionId) {
      // Set teacher as active in lernbuero (browser window open)
      api.post('/sessions/active', {
        lehrer: currentUser,
        sessionId: selectedSessionId.toString()
      }).catch(error => {
        console.error('Error setting active status:', error);
      });

      // Set up heartbeat interval
      const heartbeatInterval = setInterval(() => {
        api.post('/sessions/heartbeat', {
          lehrer: currentUser,
          sessionId: selectedSessionId.toString()
        }).catch(error => {
          console.error('Error sending heartbeat:', error);
        });
      }, 30000); // Send heartbeat every 30 seconds

      // Cleanup function to remove active status when component unmounts or session changes
      return () => {
        clearInterval(heartbeatInterval);
        // Only remove active status if component is actually unmounting, not just session changing
        // The new session will set its own active status
      };
    }
  }, [currentUser, selectedSessionId]);

  // Cleanup active status when component unmounts completely
  useEffect(() => {
    return () => {
      if (currentUser) {
        // Only remove active status when component is actually unmounting
        console.log('Component unmounting, removing active status for teacher:', currentUser);
        api.delete('/sessions/active', {
          data: { lehrer: currentUser }
        }).catch(error => {
          console.error('Error removing active status on unmount (browser window closed):', error);
        });
      }
    };
  }, [currentUser]);

  useEffect(() => {
    sessionStorage.setItem('lernbuero-display-pin', displayPin);
  }, [displayPin]);

  useEffect(() => {
    sessionStorage.setItem('lernbuero-display-dashboard-enabled', JSON.stringify(displayDashboardEnabled));
  }, [displayDashboardEnabled]);

  // Fetch today's sessions
  const { data: sessionsResponse, isLoading: sessionsLoading } = useQuery(
    ['lernbuero-sessions'],
    () => api.get('/lernbuero/sessions/today').then(res => res.data)
  );

  const sessions = sessionsResponse?.data || [];

  // Fetch attendance data when session is selected
  const { data: attendanceResponse, isLoading: attendanceLoading } = useQuery(
    ['lernbuero-attendance', selectedSessionId],
    () => api.get(`/lernbuero/attendance/${selectedSessionId}`).then(res => res.data),
    {
      enabled: !!selectedSessionId,
    }
  );

  const attendanceData = attendanceResponse?.data || [];

  const attendanceDataRef = useRef<AttendanceData[]>([]);
  useEffect(() => {
    attendanceDataRef.current = attendanceData;
  }, [attendanceData]);

  const selectedSessionIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedSessionIdRef.current = selectedSessionId;
  }, [selectedSessionId]);

  // Fotos für Attendance-Daten laden
  useEffect(() => {
    if (attendanceData.length > 0) {
      loadAllStudentPhotos(attendanceData);
    }
  }, [attendanceData]);

  // Fetch session groups from server
  const { data: groupsResponse } = useQuery(
    ['lernbuero-session-groups', selectedSessionId],
    () => api.get(`/lernbuero/sessions/${selectedSessionId}/groups`).then(res => res.data),
    { enabled: !!selectedSessionId }
  );

  useEffect(() => {
    if (groupsResponse?.data) {
      setGroups(groupsResponse.data);
    } else {
      setGroups([]);
    }
  }, [groupsResponse]);

  // Fetch panel type when session is selected
  const { data: panelTypeResponse } = useQuery(
    ['lernbuero-panel-type', selectedSessionId],
    () => api.get(`/lernbuero/panel-type/${selectedSessionId}`).then(res => res.data),
    {
      enabled: !!selectedSessionId,
    }
  );

  // Get session information for Panel B
  const { data: sessionInfo } = useQuery(
    ['session-info', selectedSessionId],
    async () => {
      if (!selectedSessionId) return null;
      const response = await api.get(`/lernbuero/sessions/${selectedSessionId}`);
      return response.data.data;
    },
    {
      enabled: !!selectedSessionId,
    }
  );

  // Aktive temporäre Abwesenheiten für die aktuelle Sitzung
  const { data: activeAbsencesResponse, refetch: refetchActiveAbsences } = useQuery(
    ['temporary-absences-active', 'lernbuero_session', selectedSessionId],
    () => fetchActiveAbsences('lernbuero_session', selectedSessionId!),
    { enabled: !!selectedSessionId }
  );
  const activeAbsencesMap = activeAbsencesResponse?.data ?? {};

  // Fetch all students when modal is opened
  const { data: allStudentsResponse, isLoading: allStudentsLoading } = useQuery(
    ['lernbuero-all-students'],
    () => api.get('/lernbuero/students/all').then(res => res.data),
    {
      enabled: showAddStudentModal,
    }
  );

  // Update panel type when response changes
  useEffect(() => {
    if (panelTypeResponse?.data?.panelType) {
      setPanelType(panelTypeResponse.data.panelType);
      sessionStorage.setItem('lernbuero-panel-type', panelTypeResponse.data.panelType);
    }
  }, [panelTypeResponse]);

  // Update all students when response changes
  useEffect(() => {
    if (allStudentsResponse?.data) {
      const studentsData = allStudentsResponse.data;
      setAvailableStudents(studentsData);
      // Fotos laden
      loadAllStudentPhotos(studentsData);
    }
  }, [allStudentsResponse]);

  // Create new session mutation
  const createSessionMutation = useMutation(
    (formData: NewSessionForm) => api.post('/lernbuero/sessions', formData),
    {
      onSuccess: (response) => {
        const newSession = response.data.data;
        setSelectedSessionId(newSession.SessionID);
        setShowNewSessionModal(false);
        setNewSessionForm({ Lehrer: '', Fach: '' });
        sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify({ Lehrer: '', Fach: '' }));
        setSessionPanelCollapsed(true); // Collapse session panel after creating session
        sessionStorage.setItem('lernbuero-session-panel-collapsed', 'true');
        queryClient.invalidateQueries(['lernbuero-sessions']);
        toast.success('Neue Sitzung erfolgreich erstellt');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler beim Erstellen der Sitzung');
      }
    }
  );

  const handleCreateSession = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSessionForm.Lehrer || !newSessionForm.Fach) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }
    createSessionMutation.mutate(newSessionForm);
  };

  const handleSessionSelect = (sessionId: number) => {
    setSelectedSessionId(sessionId);
            setSessionPanelCollapsed(true); // Collapse session panel when selecting a session
        sessionStorage.setItem('lernbuero-session-panel-collapsed', 'true');
    // Save selected session to sessionStorage
    sessionStorage.setItem('lernbuero-selected-session-id', sessionId.toString())
  };

  // Add student to attendance mutation
  const addStudentMutation = useMutation(
    (studentData: { unid: string; name: string; vorname: string; klasse: string }) =>
      api.post(`/lernbuero/attendance/${selectedSessionId}/add`, studentData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionId]);
        
        const student = response.data.student;
        const conflicts: PartnershipConflict[] = response.data?.negativePartnershipConflicts || [];
        
        if (conflicts.length > 0) {
          // Show warning modal first, don't show success toast yet
          handleStudentAdditionWarning(conflicts, {
            unid: student.unid,
            name: student.name,
            vorname: student.vorname,
            klasse: student.klasse
          });
        } else {
          // No conflicts, show success toast immediately
          if (student) {
            createAttendanceToast(student.name, student.vorname, student.memo);
          } else {
            toast.success('Schüler erfolgreich zur Anwesenheit hinzugefügt');
          }
        }

        setShowAddStudentModal(false);
        setSearchTerm('');
        sessionStorage.setItem('lernbuero-search-term', '');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Fehler beim Hinzufügen des Schülers');
      }
    }
  );

  // Remove student from attendance mutation
  const removeStudentMutation = useMutation(
    (unid: string) => api.delete(`/lernbuero/attendance/${selectedSessionId}/remove/${unid}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionId]);
        toast.success('Schüler erfolgreich aus der Anwesenheit entfernt');
      },
      onError: (error: any) => {
        console.error('Error removing student:', error);
        toast.error(error.response?.data?.message || 'Fehler beim Entfernen des Schülers');
      }
    }
  );

  const handleAddStudent = (student: AvailableStudent) => {
    addStudentMutation.mutate({
      unid: student.Unid,
      name: student.Name,
      vorname: student.Vorname,
      klasse: student.Klasse
    });
  };

  // Toggle student selection in modal
  const toggleSelectStudentInModal = (unid: string) => {
    setSelectedStudentsInModal(prev => 
      prev.includes(unid) 
        ? prev.filter(id => id !== unid) 
        : [...prev, unid]
    );
  };

  // Add multiple selected students
  const handleAddSelectedStudents = async () => {
    if (selectedStudentsInModal.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    const studentsToAdd = availableStudents.filter(student => 
      selectedStudentsInModal.includes(student.Unid) && !isStudentInAttendance(student.Unid)
    );

    if (studentsToAdd.length === 0) {
      toast.error('Die ausgewählten Schüler sind bereits hinzugefügt');
      setSelectedStudentsInModal([]);
      return;
    }

    // Add students one by one
    for (const student of studentsToAdd) {
      try {
        await api.post(`/lernbuero/attendance/${selectedSessionId}/add`, {
          unid: student.Unid,
          name: student.Name,
          vorname: student.Vorname,
          klasse: student.Klasse
        });
        
        // Show toast for each added student
        createAttendanceToast(student.Name, student.Vorname, null);
      } catch (error: any) {
        console.error('Error adding student:', error);
        toast.error(`Fehler beim Hinzufügen von ${student.Vorname} ${student.Name}`);
      }
    }

    // Invalidate queries and close modal
    queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionId]);
    setSelectedStudentsInModal([]);
    setShowAddStudentModal(false);
    setSearchTerm('');
    sessionStorage.setItem('lernbuero-search-term', '');
  };

  const handleRemoveStudent = (unid: string) => {
    if (window.confirm('Möchten Sie diesen Schüler wirklich aus der Anwesenheit entfernen?')) {
      removeStudentMutation.mutate(unid);
    }
  };

  // Multi-select handlers
  const toggleSelectStudent = (unid?: string) => {
    if (!unid) return;
    setSelectedStudentIds(prev => prev.includes(unid) ? prev.filter(id => id !== unid) : [...prev, unid]);
  };
  const areAllSelected = selectedStudentIds.length > 0 && attendanceData.every((s: any) => s.Unid && selectedStudentIds.includes(s.Unid));
  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedStudentIds([]);
    } else {
      const allIds = attendanceData.map((s: any) => s.Unid).filter(Boolean);
      setSelectedStudentIds(allIds);
    }
  };

  // Tick every second: update timer display and trigger overdue sound
  useEffect(() => {
    if (!selectedSessionId) return;
    const interval = setInterval(() => {
      setTick(t => t + 1);
      const now = Date.now();
      for (const g of groups) {
        const deadlineMs = g.deadline_ms ?? new Date(g.deadline).getTime();
        if (deadlineMs && now >= deadlineMs && !overdueNotifiedRef.current.has(g.id)) {
          overdueNotifiedRef.current.add(g.id);
          playTimerExpirationSound();
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [groups, selectedSessionId]);

  const startGroupWork = async () => {
    if (!selectedSessionId || selectedStudentIds.length < 1) return;
    const minutes = Math.max(1, Math.min(90, groupTimerMinutes || 15));
    
    try {
      await createGroupWork(minutes);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Fehler beim Starten der Gruppenarbeit');
    }
  };

  const createGroupWork = async (minutes: number) => {
    try {
      const res = await api.post(`/lernbuero/sessions/${selectedSessionId}/groups`, {
        memberIds: selectedStudentIds,
        minutes
      });
      const created: Group = res.data?.data;
      if (created) {
        setGroups(prev => [...prev, created]);
        setShowGroupTimerModal(false);
        setShowGroupWorkWarning(false);
        setSelectedStudentIds([]);
        toast.success(`Gruppenarbeit gestartet (${minutes} Min) – ${selectedStudentIds.length} Schüler`);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Fehler beim Starten der Gruppenarbeit');
    }
  };

  // Panel B Handlers
  const handleSwitchToPanelB = (student: AttendanceData) => {
    setSelectedStudentForPanelB(student);
    setPanelType('B');
    sessionStorage.setItem('lernbuero-selected-student-panel-b', JSON.stringify(student));
    sessionStorage.setItem('lernbuero-panel-type', 'B');
  };

  // Panel A Handler - aktualisiert die Tabelle beim Wechsel
  const handleSwitchToPanelA = () => {
    setPanelType('A');
    setSelectedStudentForPanelB(null);
    sessionStorage.setItem('lernbuero-panel-type', 'A');
    sessionStorage.removeItem('lernbuero-selected-student-panel-b');
    // Tabelle mit anwesenden Schülern aktualisieren
    queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionId]);
  };

  const filteredAvailableStudents = availableStudents.filter(student =>
    student.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.Vorname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.Klasse.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if a student is already in attendance
  const isStudentInAttendance = (unid: string) => {
    return attendanceData.some((student: any) => student.Unid === unid);
  };

  // Visible + auswählbare Schüler im Modal (bereits hinzugefügte werden ignoriert)
  const modalSelectableStudentIds = filteredAvailableStudents
    .filter(student => !isStudentInAttendance(student.Unid))
    .map(student => student.Unid);
  const hasModalSelectableStudents = modalSelectableStudentIds.length > 0;
  const areAllModalSelectableSelected = hasModalSelectableStudents && modalSelectableStudentIds.every(id => selectedStudentsInModal.includes(id));

  const toggleSelectAllModalStudents = () => {
    if (!hasModalSelectableStudents) return;

    setSelectedStudentsInModal(prev => {
      const currentSelection = new Set(prev);
      const allSelected = modalSelectableStudentIds.every(id => currentSelection.has(id));

      if (allSelected) {
        modalSelectableStudentIds.forEach(id => currentSelection.delete(id));
      } else {
        modalSelectableStudentIds.forEach(id => currentSelection.add(id));
      }

      return Array.from(currentSelection);
    });
  };

  const getRowColor = (anzahl: number) => {
    if (anzahl === 0) return 'bg-white dark:bg-gray-900';
    if (anzahl === 1) return 'bg-green-50 dark:bg-emerald-950/25';
    if (anzahl === 2) return 'bg-green-100 dark:bg-emerald-950/35';
    if (anzahl === 3) return 'bg-green-200 dark:bg-emerald-900/30';
    return 'bg-green-300 dark:bg-emerald-800/35';
  };

  const handleSort = (field: LernbueroPanelASortField) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      setSortDirection(newDirection)
      sessionStorage.setItem('lernbuero-sort-direction', newDirection)
    } else {
      setSortField(field)
      setSortDirection('asc')
      sessionStorage.setItem('lernbuero-sort-field', field)
      sessionStorage.setItem('lernbuero-sort-direction', 'asc')
    }
  };

  const getSortIcon = (field: LernbueroPanelASortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600 dark:text-primary-400" />
      : <ArrowDown className="h-4 w-4 text-primary-600 dark:text-primary-400" />;
  };

  // Handler für Student Info Modal
  const handleShowStudentInfo = (student: AttendanceData) => {
    setSelectedStudentForInfo(student);
    setShowStudentInfoModal(true);
  };

  const handleMemoClick = (student: AttendanceData) => {
    setSelectedStudentForMemo(student);
    setShowMemoModal(true);
  };

  const handleEmailClick = async (student: AttendanceData) => {
    try {
      if (!student.Unid) {
        toast.error('Student-ID fehlt für E-Mail-Versand');
        return;
      }

      // Pre-check: sender name available in personal settings
      let profile: any = null;
      try {
        const profRes = await api.get('/profile');
        profile = profRes.data?.data;
        const fn = (profile?.firstName || '').toString().trim();
        const ln = (profile?.lastName || '').toString().trim();
        const dn = (profile?.displayName || '').toString().trim();
        const hasName = Boolean(`${fn} ${ln}`.trim()) || Boolean(dn);
        if (!hasName) {
          setPendingEmailStudent(student);
          setShowPersonalDataModal(true);
          return;
        }
      } catch {
        // If profile fetch fails, continue with fallback (username)
      }

      // Pre-check: user email credentials exist
      try {
        const credRes = await api.get('/email/credentials');
        const creds = credRes.data?.data || [];
        const hasEnabled = creds.some((c: any) => c.enabled);
        if (!hasEnabled) {
          setPendingEmailStudent(student);
          setShowEmailCredentialModal(true);
          return;
        }
      } catch {
        toast.error('E-Mail-Konten konnten nicht geprüft werden');
        return;
      }

      // Merke ausgewählten Schüler für Modal (liefert Unid an KI)
      setSelectedStudentForInfo(student);

      // Hole Eltern-E-Mail-Adressen für den Schüler
      const res = await api.get(`/email/parents/${student.Unid}`);
      const parentEmailsData = res.data?.data;
      const parentEmails = parentEmailsData?.parentEmails || [];
      
      if (parentEmails.length === 0) {
        toast.error('Keine Eltern-E-Mail-Adressen für diesen Schüler hinterlegt');
        return;
      }

      // E-Mail-Entwurf ohne Übernahme der Lernbüro-Notizen
      const subject = `Nachricht zu ${student.Vorname} ${student.Name}`;
      let body = `Sehr geehrte Eltern,\n\n`;
      body += `bezüglich Ihres Kindes ${student.Vorname} ${student.Name} (${student.Klasse}).\n\n`;
      const fn = (profile?.firstName || user?.first_name || '').toString().trim();
      const ln = (profile?.lastName || user?.last_name || '').toString().trim();
      const dn = (profile?.displayName || user?.displayName || '').toString().trim();
      const senderName = `${fn} ${ln}`.trim() || dn || user?.username || 'Lernrakete System';
      body += `Mit freundlichen Grüßen\n${senderName}`;

      // Zeige alle verfügbaren Eltern-E-Mails an
      const emailDisplay = parentEmails.length === 1 
        ? parentEmails[0]
        : `${parentEmails.join('; ')} (${parentEmails.length} Adressen)`;

      setEmailRecipient(emailDisplay);
      setEmailStudentName(`${student.Vorname} ${student.Name} (${student.Klasse})`);
      setEmailInitialSubject(subject);
      setEmailInitialBody(body);
      setShowEmailModal(true);
    } catch (error: any) {
      console.error('Error preparing email:', error);
      toast.error(error?.response?.data?.message || 'Fehler beim Vorbereiten der E-Mail');
    }
  };

  const sortedAttendanceData = useMemo(() => {
    const compareNameVorname = (a: AttendanceData, b: AttendanceData) => {
      const byName = String(a.Name ?? '').localeCompare(String(b.Name ?? ''), 'de', { sensitivity: 'base' })
      if (byName !== 0) return byName
      return String(a.Vorname ?? '').localeCompare(String(b.Vorname ?? ''), 'de', { sensitivity: 'base' })
    }

    const cmpNum = (a: number, b: number, x: AttendanceData, y: AttendanceData) => {
      if (a < b) return sortDirection === 'asc' ? -1 : 1
      if (a > b) return sortDirection === 'asc' ? 1 : -1
      return compareNameVorname(x, y)
    }

    const cmpStr = (a: string, b: string, x: AttendanceData, y: AttendanceData) => {
      if (a < b) return sortDirection === 'asc' ? -1 : 1
      if (a > b) return sortDirection === 'asc' ? 1 : -1
      return compareNameVorname(x, y)
    }

    const isSelected = (s: AttendanceData) => Boolean(s.Unid && selectedStudentIds.includes(s.Unid))
    const hasFotoLoaded = (s: AttendanceData) => hasPhoto(studentPhotos[getStudentId(s)])
    const belegeAbschnitt = (s: AttendanceData) => Number(s.BelegeAbschnitt ?? 0)

    return [...attendanceData].sort((a, b) => {
      switch (sortField) {
        case 'Auswahl':
          return cmpNum(isSelected(a) ? 1 : 0, isSelected(b) ? 1 : 0, a, b)
        case 'Foto':
          return cmpNum(hasFotoLoaded(a) ? 1 : 0, hasFotoLoaded(b) ? 1 : 0, a, b)
        case 'Name':
          return cmpStr(String(a.Name ?? '').toLowerCase(), String(b.Name ?? '').toLowerCase(), a, b)
        case 'Vorname':
          return cmpStr(String(a.Vorname ?? '').toLowerCase(), String(b.Vorname ?? '').toLowerCase(), a, b)
        case 'Klasse':
          return cmpStr(String(a.Klasse ?? '').toLowerCase(), String(b.Klasse ?? '').toLowerCase(), a, b)
        case 'BelegeAbschnitt':
          return cmpNum(belegeAbschnitt(a), belegeAbschnitt(b), a, b)
        case 'Anzahl':
          return cmpNum(Number(a.Anzahl ?? 0), Number(b.Anzahl ?? 0), a, b)
        case 'Aktionen': {
          const memoA = (a.Memo || '').trim()
          const memoB = (b.Memo || '').trim()
          const primary = memoA.localeCompare(memoB, 'de', { sensitivity: 'base' })
          if (primary !== 0) return sortDirection === 'asc' ? primary : -primary
          const absenceA = a.Unid && activeAbsencesMap[a.Unid] ? 1 : 0
          const absenceB = b.Unid && activeAbsencesMap[b.Unid] ? 1 : 0
          if (absenceA !== absenceB) return cmpNum(absenceA, absenceB, a, b)
          return compareNameVorname(a, b)
        }
        default:
          return compareNameVorname(a, b)
      }
    })
  }, [
    attendanceData,
    sortField,
    sortDirection,
    studentPhotos,
    selectedStudentIds,
    activeAbsencesMap,
  ])

  // Handle group work warning (no time limit)
  const handleGroupWorkWarning = useCallback((conflicts: PartnershipConflict[], ids: string[]) => {
    setGroupWorkConflicts(conflicts)
    const namesMap: Record<string, string> = {}

    conflicts.forEach(conflict => {
      if (conflict.schueler1_vorname && conflict.schueler1_name) {
        namesMap[conflict.schueler1_id] = `${conflict.schueler1_vorname} ${conflict.schueler1_name}`
      }
      if (conflict.schueler2_vorname && conflict.schueler2_name) {
        namesMap[conflict.schueler2_id] = `${conflict.schueler2_vorname} ${conflict.schueler2_name}`
      }
    })

    ids.forEach(id => {
      if (namesMap[id]) return
      const student = attendanceData.find((s: AttendanceData) => {
        const identifiers = [
          s.Unid,
          s.sophomorixUnid,
          s.ID ? String(s.ID) : undefined
        ].filter(Boolean)
        return identifiers.includes(id)
      })
      if (student) {
        namesMap[id] = `${student.Vorname} ${student.Name}`
      }
    })

    setGroupWorkConflictNameMap(namesMap)
    setShowGroupWorkWarning(true)
  }, [attendanceData])

  // Handle individual student addition warning (6 second timeout)
  const handleStudentAdditionWarning = useCallback((conflicts: PartnershipConflict[], studentData: {unid: string, name: string, vorname: string, klasse: string}) => {
    setStudentAdditionConflicts(conflicts)
    setPendingStudentData(studentData)
    const namesMap: Record<string, string> = {}

    conflicts.forEach(conflict => {
      if (conflict.schueler1_vorname && conflict.schueler1_name) {
        namesMap[conflict.schueler1_id] = `${conflict.schueler1_vorname} ${conflict.schueler1_name}`
      }
      if (conflict.schueler2_vorname && conflict.schueler2_name) {
        namesMap[conflict.schueler2_id] = `${conflict.schueler2_vorname} ${conflict.schueler2_name}`
      }
    })

    // Add the new student to the name map
    namesMap[studentData.unid] = `${studentData.vorname} ${studentData.name}`

    // Add existing students from attendance data
    attendanceDataRef.current.forEach((student: AttendanceData) => {
      const identifiers = [
        student.Unid,
        student.sophomorixUnid,
        student.ID ? String(student.ID) : undefined
      ].filter(Boolean)
      
      identifiers.forEach(id => {
        if (id && !namesMap[id]) {
          namesMap[id] = `${student.Vorname} ${student.Name}`
        }
      })
    })

    setStudentAdditionConflictNameMap(namesMap)
    setShowStudentAdditionWarning(true)
    
    // Set 6 second timeout
    if (studentWarningTimeoutRef.current) {
      window.clearTimeout(studentWarningTimeoutRef.current)
    }
    studentWarningTimeoutRef.current = window.setTimeout(() => {
      setShowStudentAdditionWarning(false)
      setStudentAdditionConflicts([])
      setPendingStudentData(null)
    }, 6000)
  }, [])

  const handleAttendanceUpdate = useCallback((update: AttendanceUpdate) => {
    console.log('📱 Real-time attendance update received in Lernbuero:', update)

    if (update.negativePartnershipConflicts && update.negativePartnershipConflicts.length > 0) {
      handleStudentAdditionWarning(update.negativePartnershipConflicts, {
        unid: update.student.unid,
        name: update.student.name,
        vorname: update.student.vorname,
        klasse: update.student.klasse
      })
    }
  }, [handleStudentAdditionWarning])

  const handlePanelSwitchUpdate = useCallback((update: PanelSwitchUpdate) => {
    console.log('🔄 Panel switch update received:', update)

    if (update.action === 'switch_to_panel_b') {
      const student = attendanceDataRef.current.find((s: AttendanceData) => s.Unid === update.student.unid)

      if (student) {
        setSelectedStudentForPanelB(student)
        setPanelType('B')

        sessionStorage.setItem('lernbuero-selected-student-panel-b', JSON.stringify(student))
        sessionStorage.setItem('lernbuero-panel-type', 'B')

        console.log(`✅ Automatically switched to Panel B for student ${student.Vorname} ${student.Name}`)
      }
    } else if (update.action === 'switch_to_panel_a') {
      setPanelType('A')
      setSelectedStudentForPanelB(null)

      sessionStorage.setItem('lernbuero-panel-type', 'A')
      sessionStorage.removeItem('lernbuero-selected-student-panel-b')

      // Tabelle mit anwesenden Schülern aktualisieren
      queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionIdRef.current])

      console.log('✅ Automatically switched to Panel A (card removed)')
    }
  }, [setPanelType, setSelectedStudentForPanelB, queryClient])

  const handleToastDismissUpdate = useCallback((update: ToastDismissUpdate) => {
    if (update.sessionId === selectedSessionIdRef.current) {
      setGroups(prev => prev.filter(x => x.id !== update.groupId))
      overdueNotifiedRef.current.delete(update.groupId)
    }
  }, [setGroups])

  const { isConnected } = useAttendanceUpdates({
    enabled: !!currentUser,
    onUpdate: handleAttendanceUpdate,
    onPanelSwitch: handlePanelSwitchUpdate,
    onToastDismiss: handleToastDismissUpdate
  })

  useEffect(() => {
    console.log('🔍 SSE Connection Status:', {
      currentUser,
      isConnected,
      enabled: !!currentUser
    })
  }, [currentUser, isConnected])

  useEffect(() => {
    let active = true;
    const loadDisplayConnection = async () => {
      try {
        const connection = await fetchMyDisplayConnection();
        if (!active) return;
        const pin = connection?.pin || null;
        setDisplayConnectedPin(pin);
        if (pin) setDisplayPin(pin);
      } catch (error) {
        console.warn('Display connection fetch failed', error);
      }
    };
    loadDisplayConnection();
    return () => {
      active = false;
    };
  }, []);

  const handleConnectDisplay = useCallback(async () => {
    const pin = displayPin.trim().toUpperCase();
    if (!pin || pin.length !== 6) {
      toast.error('Bitte eine gueltige 6-stellige PIN eingeben');
      return;
    }
    setDisplayConnectBusy(true);
    try {
      await connectDisplay({
        pin,
        context: { source: 'lernbuero' }
      });
      setDisplayPin(pin);
      setDisplayConnectedPin(pin);
      toast.success(`Display ${pin} gekoppelt`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Display konnte nicht gekoppelt werden');
    } finally {
      setDisplayConnectBusy(false);
    }
  }, [displayPin]);

  const handleDisconnectDisplay = useCallback(async () => {
    if (!displayConnectedPin) return;
    setDisplayConnectBusy(true);
    try {
      await teacherUpdateLernbueroDashboard({
        pin: displayConnectedPin,
        dashboard: { enabled: false, sessionId: null, sessionLabel: '', students: [], top10: [], groups: [], serverNowMs: Date.now() }
      });
      await disconnectDisplay(displayConnectedPin);
      setDisplayConnectedPin(null);
      toast.success('Display entkoppelt');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Display konnte nicht entkoppelt werden');
    } finally {
      setDisplayConnectBusy(false);
    }
  }, [displayConnectedPin]);

  useEffect(() => {
    if (!displayConnectedPin) return;
    const activeSession = sessions.find((s: Session) => s.SessionID === selectedSessionId);
    const attendanceSortedByCount = [...attendanceData].sort((a, b) => {
      const byCount = Number(b.Anzahl || 0) - Number(a.Anzahl || 0);
      if (byCount !== 0) return byCount;
      const byLast = String(a.Name || '').localeCompare(String(b.Name || ''), 'de');
      if (byLast !== 0) return byLast;
      return String(a.Vorname || '').localeCompare(String(b.Vorname || ''), 'de');
    });
    let currentRank = 0;
    let lastScore: number | null = null;
    const top10Candidates = attendanceSortedByCount.filter((student) => Number(student.Anzahl || 0) > 0);
    const top10 = top10Candidates.slice(0, 10).map((student, index) => {
      const score = Number(student.Anzahl || 0);
      if (lastScore === null || score !== lastScore) currentRank = index + 1;
      lastScore = score;
      return {
        rank: currentRank,
        unid: student.Unid || student.sophomorixUnid || String(student.ID || ''),
        firstName: student.Vorname || '',
        lastName: student.Name || '',
        className: student.Klasse || '',
        count: score
      };
    });
    const groupRows = groups.map((group) => ({
      id: group.id,
      colorClass: group.color_class,
      deadlineMs: group.deadline_ms ?? new Date(group.deadline).getTime(),
      members: attendanceData
        .filter((student: AttendanceData) => group.memberIds.includes(student.Unid || ''))
        .map((student: AttendanceData) => ({
          unid: student.Unid || student.sophomorixUnid || String(student.ID || ''),
          firstName: student.Vorname || '',
          lastName: student.Name || '',
          className: student.Klasse || '',
          count: Number(student.Anzahl || 0)
        }))
    }));
    type DashboardAbsence = {
      unid: string;
      firstName: string;
      lastName: string;
      className: string;
      startedAt: string;
      startedAtMs: number;
    };
    const absences: DashboardAbsence[] = attendanceData
      .map((student: AttendanceData): DashboardAbsence | null => {
        const unid = student.Unid || student.sophomorixUnid || String(student.ID || '');
        if (!unid) return null;
        const active = activeAbsencesMap[unid];
        if (!active?.started_at) return null;
        return {
          unid,
          firstName: student.Vorname || '',
          lastName: student.Name || '',
          className: student.Klasse || '',
          startedAt: active.started_at,
          startedAtMs: parseAbsenceStartedAtMs(active.started_at)
        };
      })
      .filter((item: DashboardAbsence | null): item is DashboardAbsence => Boolean(item))
      .sort((a: DashboardAbsence, b: DashboardAbsence) => a.startedAtMs - b.startedAtMs);

    const payload = {
      pin: displayConnectedPin,
      dashboard: {
        enabled: Boolean(displayDashboardEnabled && selectedSessionId),
        sessionId: selectedSessionId,
        sessionLabel: activeSession ? `${activeSession.Fach} (${activeSession.Lehrer})` : '',
        updatedAt: new Date().toISOString(),
        serverNowMs: Date.now(),
        students: attendanceData.map((student: AttendanceData) => ({
          unid: student.Unid || student.sophomorixUnid || String(student.ID || ''),
          firstName: student.Vorname || '',
          lastName: student.Name || '',
          className: student.Klasse || '',
          count: Number(student.Anzahl || 0)
        })),
        top10,
        groups: groupRows,
        absences
      }
    };

    const timeoutId = window.setTimeout(() => {
      teacherUpdateLernbueroDashboard(payload).catch((error) => {
        console.warn('Display dashboard update failed', error);
      });
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [displayConnectedPin, displayDashboardEnabled, selectedSessionId, panelType, attendanceData, groups, sessions, activeAbsencesMap]);

  // Handle removing student when "Wegschicken" is clicked
  const handleRemoveStudentFromWarning = useCallback(async (studentData: {unid: string, name: string, vorname: string, klasse: string}) => {
    try {
      await api.delete(`/lernbuero/attendance/${selectedSessionId}/remove/${studentData.unid}`);
      queryClient.invalidateQueries(['lernbuero-attendance', selectedSessionId]);
      toast.success(`${studentData.vorname} ${studentData.name} wurde aus der Anwesenheit entfernt`);
    } catch (error: any) {
      console.error('Error removing student:', error);
      toast.error('Fehler beim Entfernen des Schülers');
    }
  }, [selectedSessionId, queryClient]);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header - Kompakt bei Panel B */}
      {panelType === 'B' ? (
        <div className="mb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <div className="min-w-0">
                <h1 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50 sm:text-xl">
                  {selectedStudentForPanelB ? 
                    `${selectedStudentForPanelB.Vorname} ${selectedStudentForPanelB.Name} (${selectedStudentForPanelB.Klasse})` : 
                    'Panel B'
                  }
                </h1>
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                  {sessions.find((s: any) => s.SessionID === selectedSessionId)?.Fach} 
                  {' '}(<span className="whitespace-nowrap">{sessions.find((s: any) => s.SessionID === selectedSessionId)?.Lehrer}</span>)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {isConnected ? 'Live' : 'Offline'}
                </span>
              </div>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={handleSwitchToPanelA}>
              ← Zurück zu Panel A
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">Lernbüro</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Verwalten Sie Lehrveranstaltungen und Anwesenheit.
          </p>
        </div>
      )}

      {/* Session Selection - Nur bei Panel A */}
      {panelType === 'A' && (
        <div className="card p-6 mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => {
                const newCollapsed = !sessionPanelCollapsed;
                setSessionPanelCollapsed(newCollapsed);
                sessionStorage.setItem('lernbuero-session-panel-collapsed', newCollapsed.toString());
              }}
              className="rounded p-1 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              aria-expanded={!sessionPanelCollapsed}
            >
              {sessionPanelCollapsed ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronUp className="h-5 w-5" />
              )}
            </button>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Sitzung auswählen</h2>
              {sessionPanelCollapsed && selectedSessionId && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Aktuelle Sitzung: {sessions.find((s: any) => s.SessionID === selectedSessionId)?.Fach} 
                  {' '}({sessions.find((s: any) => s.SessionID === selectedSessionId)?.Lehrer})
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              const newForm = { 
                Lehrer: currentUser || '', 
                Fach: '' 
              };
              setNewSessionForm(newForm);
              sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify(newForm));
              setShowNewSessionModal(true);
            }}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
            Neue Sitzung
          </Button>
        </div>

        {!sessionPanelCollapsed && (
          <>
            {sessionsLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Calendar className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="font-medium text-gray-700 dark:text-gray-300">Keine Sitzungen für heute gefunden</p>
                <p className="mt-1">Erstellen Sie eine neue Sitzung, um zu beginnen.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sessions.map((session: Session) => (
                  <button
                    type="button"
                    key={session.SessionID}
                    onClick={() => handleSessionSelect(session.SessionID)}
                    className={`rounded-lg border-2 p-4 text-left transition-all ${
                      selectedSessionId === session.SessionID
                        ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-950/40'
                        : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-primary-600 dark:hover:bg-gray-800/80'
                    }`}
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-50">{session.Fach}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{session.Lehrer}</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(session.TimeStamp).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      
                      <div className="mt-3 border-t border-gray-200 pt-2 dark:border-gray-600">
                        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span>{session.studentCount} Schüler</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <BookOpen className="h-3 w-3" />
                            <span>{session.evidenceCount} Belege</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      )}

      {/* Main Panel */}
      {selectedSessionId && (
        <div className={`card ${panelType === 'B' ? 'p-4' : 'p-6'}`}>
          {panelType === 'A' && (
            <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Panel A — Sitzung {selectedSessionId}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Anwesenheit und Aktionen; Schriftgröße und Farbschema wie unter Darstellung in /Showcase.
                </p>
              </div>
              <div className="flex flex-col gap-3 2xl:flex-row 2xl:flex-wrap 2xl:items-center 2xl:justify-end">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Panel</span>
                  <Badge variant="info" className="tabular-nums">
                    {panelType}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {isConnected ? 'Live-Updates aktiv' : 'Live-Updates inaktiv'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-600 dark:bg-gray-800/80">
                  <Tv className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                  <input
                    value={displayPin}
                    onChange={(event) => setDisplayPin(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                    placeholder="PIN"
                    className="input-field w-20 px-2 py-1 text-xs"
                  />
                  {!displayConnectedPin ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="primary"
                      onClick={handleConnectDisplay}
                      disabled={displayConnectBusy}
                    >
                      Koppeln
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="xs"
                      variant="secondary"
                      onClick={handleDisconnectDisplay}
                      disabled={displayConnectBusy}
                    >
                      Entkoppeln
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="xs"
                    variant={displayDashboardEnabled ? 'primary' : 'outline'}
                    onClick={() => setDisplayDashboardEnabled((prev) => !prev)}
                    disabled={!displayConnectedPin}
                    title="Permanente Lernbuero-Ansicht auf dem Display ein/aus"
                  >
                    Display {displayDashboardEnabled ? 'AN' : 'AUS'}
                  </Button>
                </div>
               
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setShowAddStudentModal(true)}
                  className="shrink-0"
                >
                  <UserPlus className="h-5 w-5" />
                  Schüler hinzufügen
                </Button>
              </div>
            </div>
          )}

          {panelType === 'A' ? (
            /* Panel A - Attendance Data */
            <div>
              {attendanceLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
                </div>
              ) : attendanceData.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  <Users className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                  <p>Keine Anwesenheitsdaten für diese Sitzung gefunden.</p>
                </div>
              ) : (
                <div className="min-w-0 space-y-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Badge variant="neutral" className="tabular-nums">
                        Ausgewählt: {selectedStudentIds.length}
                      </Badge>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowGroupsModal(true)}
                        className={
                          groups.some((g) =>
                            getRemainingTime(g.deadline_ms ?? new Date(g.deadline).getTime()).overdue
                          )
                            ? 'animate-pulse border-red-400 text-red-800 hover:bg-red-50 dark:border-red-600 dark:text-red-200 dark:hover:bg-red-950/40'
                            : ''
                        }
                        title="Gruppen/Extern anzeigen"
                      >
                        Gruppen/Extern: {groups.length}
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        onClick={async () => {
                          if (selectedStudentIds.length < 1) {
                            toast.error('Bitte mindestens 1 Schüler auswählen');
                            return;
                          }
                          
                          try {
                            const checkRes = await api.post('/lernpartnerschaften/check', { student_ids: selectedStudentIds });
                            const conflicts: PartnershipConflict[] = checkRes.data?.data || [];
                            
                            if (conflicts.length > 0) {
                              handleGroupWorkWarning(conflicts, selectedStudentIds);
                              return;
                            }
                            setShowGroupTimerModal(true);
                          } catch (e) {
                            setShowGroupTimerModal(true);
                          }
                        }}
                        disabled={selectedStudentIds.length < 1}
                        title={selectedStudentIds.length < 1 ? "Mindestens 1 Schüler auswählen" : "Gruppenarbeit starten (Timer)"}
                      >
                        <Timer className="h-4 w-4" />
                        Gruppenarbeit starten
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={async () => {
                          try {
                            if (selectedStudentIds.length < 2) {
                              toast.error('Für Lernbeziehungen müssen mindestens 2 Schüler ausgewählt werden');
                              return;
                            }
                            await api.post('/lernpartnerschaften/bulk', {
                              student_ids: selectedStudentIds,
                              gewichtung: 50,
                              beschreibung: 'Günstige Lernbeziehung aus Lernbüro',
                              halbwertszeit_tage: 60
                            });
                            toast.success('Günstige Lernbeziehung gespeichert');
                          } catch (e: any) {
                            toast.error(e?.response?.data?.message || 'Fehler beim Speichern der Lernbeziehung');
                          }
                        }}
                        disabled={selectedStudentIds.length < 2}
                        title={selectedStudentIds.length < 2 ? "Mindestens 2 Schüler für Lernbeziehungen auswählen" : "Positive Lernpartnerschaften anlegen"}
                      >
                        <ThumbsUp className="h-4 w-4" />
                        Günstige Lernbeziehung
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={async () => {
                          try {
                            if (selectedStudentIds.length < 2) {
                              toast.error('Für Lernbeziehungen müssen mindestens 2 Schüler ausgewählt werden');
                              return;
                            }
                            await api.post('/lernpartnerschaften/bulk', {
                              student_ids: selectedStudentIds,
                              gewichtung: -50,
                              beschreibung: 'Ungünstige Lernbeziehung aus Lernbüro',
                              halbwertszeit_tage: 60
                            });
                            toast.success('Ungünstige Lernbeziehung gespeichert');
                          } catch (e: any) {
                            toast.error(e?.response?.data?.message || 'Fehler beim Speichern der Lernbeziehung');
                          }
                        }}
                        disabled={selectedStudentIds.length < 2}
                        title={selectedStudentIds.length < 2 ? "Mindestens 2 Schüler für Lernbeziehungen auswählen" : "Negative Lernpartnerschaften anlegen"}
                      >
                        <ThumbsDown className="h-4 w-4" />
                        Ungünstige Lernbeziehung
                      </Button>
                    </div>
                  </div>
                  <TableScrollArea>
                  <Table className="min-w-[960px]">
                     <TableHead>
                                               <TableRow className="border-0 hover:bg-transparent dark:hover:bg-transparent">
                          <TableHeaderCell className="w-[4.5rem] py-3 align-middle normal-case">
                            <div className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={areAllSelected}
                                onChange={toggleSelectAll}
                                aria-label="Alle auswählen"
                              />
                              <button
                                type="button"
                                onClick={() => handleSort('Auswahl')}
                                className="rounded p-1 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                                title="Nach Markierung sortieren"
                                aria-label="Nach Markierung sortieren"
                              >
                                {getSortIcon('Auswahl')}
                              </button>
                            </div>
                          </TableHeaderCell>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Foto"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="normal-case"
                           >
                             Foto
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Name"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="normal-case"
                           >
                             Name
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Vorname"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="normal-case"
                           >
                             Vorname
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Klasse"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="normal-case"
                           >
                             Klasse
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="BelegeAbschnitt"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="max-w-[10rem] whitespace-normal leading-snug normal-case"
                           >
                             Belege im aktuellen Abschnitt
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Anzahl"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="normal-case"
                           >
                             Belege heute
                           </SortableTh>
                           <SortableTh<LernbueroPanelASortField>
                             sortKey="Aktionen"
                             activeKey={sortField}
                             direction={sortDirection}
                             onSort={handleSort}
                             className="min-w-[200px] normal-case"
                           >
                             Aktionen
                           </SortableTh>
                         </TableRow>
                     </TableHead>
                     <TableBody>
                      {sortedAttendanceData.map((student: AttendanceData, index: number) => (
                        <TableRow 
                          key={index}
                          className={`border-b border-gray-100 dark:border-gray-800 ${getRowColor(student.Anzahl)} ${selectedStudentIds.includes(student.Unid || '') ? '!bg-blue-50 dark:!bg-blue-950/45' : ''}`}
                        >
                          <TableCell className={`py-3 align-middle ${(() => {
                            const g = groups.find(x => x.memberIds.includes(student.Unid || ''));
                            return g ? `border-l-4 ${g.color_class}` : '';
                          })()}`}>
                            <input
                              type="checkbox"
                              checked={selectedStudentIds.includes(student.Unid || '')}
                              onChange={() => toggleSelectStudent(student.Unid)}
                            />
                          </TableCell>
                          <TableCell className="py-3">
                            {hasPhoto(studentPhotos[getStudentId(student)]) ? (
                              <img
                                src={createPhotoDataUrl(studentPhotos[getStudentId(student)]!)}
                                alt={`Foto von ${student.Name} ${student.Vorname}`}
                                className="relative h-20 w-20 cursor-zoom-in rounded-full object-cover transition-transform duration-300 ease-out hover:z-10 hover:scale-[1.5] hover:shadow-lg"
                              />
                            ) : (
                              <div className="relative flex h-20 w-20 cursor-zoom-in items-center justify-center rounded-full bg-gray-200 transition-transform duration-300 ease-out hover:z-10 hover:scale-[1.5] hover:shadow-lg dark:bg-gray-700">
                                <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="py-3 font-medium">{student.Name}</TableCell>
                          <TableCell className="py-3 text-gray-700 dark:text-gray-300">{student.Vorname}</TableCell>
                          <TableCell className="py-3 text-gray-700 dark:text-gray-300">{student.Klasse}</TableCell>
                          <TableCell className="py-3 tabular-nums text-gray-800 dark:text-gray-200">
                            {Number(student.BelegeAbschnitt ?? 0)}
                          </TableCell>
                                                                                <TableCell className="py-3">
                             <Badge
                               variant={
                                 student.Anzahl === 0
                                   ? 'neutral'
                                   : student.Anzahl <= 2
                                     ? 'success'
                                     : 'info'
                               }
                               className="tabular-nums"
                             >
                               {student.Anzahl}
                             </Badge>
                           </TableCell>
                           <TableCell className="min-w-[200px] py-3">
                             <div className="flex flex-wrap items-center gap-1">
                               {student.Unid && (
                                 <Button
                                   type="button"
                                   variant="ghost"
                                   size="sm"
                                   className="min-h-0 px-2 py-1"
                                   onClick={() => handleShowStudentInfo(student)}
                                   title="Schülerinformationen anzeigen"
                                 >
                                   <Info className="h-4 w-4" />
                                 </Button>
                               )}
                               <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 className={`min-h-0 px-2 py-1 ${
                                   student.Memo && student.Memo.trim() !== ''
                                     ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                                     : 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30'
                                 }`}
                                 title={
                                   student.Memo && student.Memo.trim() !== ''
                                     ? 'Memo bearbeiten (Inhalt vorhanden)'
                                     : 'Memo erstellen (leer)'
                                 }
                                 onClick={() => handleMemoClick(student)}
                               >
                                 <StickyNote className="h-4 w-4" />
                               </Button>
                               <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 className="min-h-0 px-2 py-1 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40"
                                 onClick={() => handleEmailClick(student)}
                                 title="E-Mail an Schüler senden"
                               >
                                 <Mail className="h-4 w-4" />
                               </Button>
                               <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="min-h-0 px-2 py-1 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                                  onClick={() => handleSwitchToPanelB(student)}
                                  title="Zu Panel B wechseln - Aufgaben für diesen Schüler anzeigen"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                               {selectedSessionId && student.Unid && (
                                 <AbsenceToggle
                                   contextType="lernbuero_session"
                                   contextId={selectedSessionId}
                                   studentUnid={student.Unid}
                                   activeAbsence={activeAbsencesMap[student.Unid] ?? null}
                                   onStarted={() => refetchActiveAbsences()}
                                   onEnded={() => refetchActiveAbsences()}
                                   compact
                                 />
                               )}
                               <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 className="min-h-0 px-2 py-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                 onClick={() => handleRemoveStudent(student.Unid || '')}
                                 disabled={removeStudentMutation.isLoading}
                                 title="Schüler aus Lernbüro entfernen"
                               >
                                 <UserMinus className="h-4 w-4" />
                               </Button>
                             </div>
                           </TableCell>
                         </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableScrollArea>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                    Panel B — Lernbüro-Aufgaben
                  </h3>
                  {selectedStudentForPanelB && (
                    <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                      {selectedStudentForPanelB.Vorname} {selectedStudentForPanelB.Name},{' '}
                      {selectedStudentForPanelB.Klasse}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleSwitchToPanelA}
                  className="shrink-0 self-start"
                  title="Zurück zu Panel A"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {selectedSessionId && sessionInfo && selectedStudentForPanelB ? (
                <LernbueroTasks
                  sessionId={selectedSessionId}
                  unid={selectedStudentForPanelB.Unid || ''}
                  sessionFach={sessionInfo.Fach}
                />
              ) : (
                <div className="py-12 text-center">
                  <AlertTriangle className="mx-auto mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">Kein Schüler ausgewählt</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Bitte wählen Sie einen Schüler in Panel A aus, um dessen Aufgaben anzuzeigen.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={showAddStudentModal}
        onClose={() => {
          setShowAddStudentModal(false);
          setSelectedStudentsInModal([]);
        }}
        title="Schüler zur Anwesenheit hinzufügen"
        description="Suche, Auswahl und Hinzufügen — Darstellung folgt den UI-Regeln (Hell/Dunkel, Schriftgrad über Darstellung)."
        size="xl"
        panelClassName="max-h-[80vh] overflow-hidden flex flex-col"
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddStudentModal(false);
                setSelectedStudentsInModal([]);
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleAddSelectedStudents}
              disabled={selectedStudentsInModal.length === 0}
            >
              <UserPlus className="h-4 w-4" />
              Ausgewählte hinzufügen
              {selectedStudentsInModal.length > 0 ? ` (${selectedStudentsInModal.length})` : ''}
            </Button>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-4">
               <div className="relative min-w-[12rem] flex-1">
                 <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                 <input
                   type="text"
                   value={searchTerm}
                   onChange={(e) => {
                     setSearchTerm(e.target.value);
                     sessionStorage.setItem('lernbuero-search-term', e.target.value);
                   }}
                   className="input-field w-full pl-10"
                   placeholder="Nach Name, Vorname oder Klasse suchen…"
                 />
               </div>
               {selectedStudentsInModal.length > 0 && (
                 <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                   {selectedStudentsInModal.length} ausgewählt
                 </div>
               )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={toggleSelectAllModalStudents}
                disabled={!hasModalSelectableStudents}
                title={hasModalSelectableStudents ? 'Alle sichtbaren Schüler auswählen' : 'Keine auswählbaren Schüler vorhanden'}
              >
                <CheckSquare className="h-4 w-4" />
                {areAllModalSelectableSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
              </Button>
             </div>

              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {allStudentsLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
                  </div>
                ) : filteredAvailableStudents.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    <Users className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <p>{searchTerm ? 'Keine Schüler gefunden' : 'Keine Schüler verfügbar'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAvailableStudents.map((student: AvailableStudent) => {
                      const isInAttendance = isStudentInAttendance(student.Unid);
                      const isSelected = selectedStudentsInModal.includes(student.Unid);
                      const studentId = getStudentId(student);
                      const studentPhoto = studentPhotos[studentId];
                      const hasStudentPhoto = hasPhoto(studentPhoto);
                      const studentPhotoUrl = hasStudentPhoto && studentPhoto ? createPhotoDataUrl(studentPhoto) : null;
                      return (
                        <div
                          key={student.Unid}
                          className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                            isInAttendance
                              ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/35'
                              : isSelected
                              ? 'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-950/40'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-primary-600 dark:hover:bg-gray-800/80'
                          }`}
                          onClick={() => !isInAttendance && toggleSelectStudentInModal(student.Unid)}
                        >
                          <div className="mb-3 flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (!isInAttendance) toggleSelectStudentInModal(student.Unid);
                              }}
                              disabled={isInAttendance}
                              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                            />
                            <div className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                              {hasStudentPhoto && studentPhotoUrl ? (
                                <img
                                  src={studentPhotoUrl}
                                  alt={`Foto von ${student.Vorname} ${student.Name}`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium text-gray-900 dark:text-gray-50">{student.Name}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{student.Vorname}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{student.Klasse}</p>
                            </div>
                          </div>
                          {isInAttendance ? (
                            <div className="flex w-full items-center justify-center gap-2 rounded bg-emerald-100 px-3 py-2 text-sm font-medium text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100">
                              <UserPlus className="h-4 w-4" />
                              Bereits hinzugefügt
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="primary"
                              className="w-full"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddStudent(student);
                              }}
                              disabled={addStudentMutation.isLoading}
                            >
                              {addStudentMutation.isLoading ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <UserPlus className="h-4 w-4" />
                                  Hinzufügen
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
        </div>
      </Modal>

       <Modal
        isOpen={showNewSessionModal}
        onClose={() => {
          setShowNewSessionModal(false);
          const resetForm = { Lehrer: '', Fach: '' };
          setNewSessionForm(resetForm);
          sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify(resetForm));
        }}
        title="Neue Sitzung erstellen"
        size="md"
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowNewSessionModal(false);
                const resetForm = { Lehrer: '', Fach: '' };
                setNewSessionForm(resetForm);
                sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify(resetForm));
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              form="lernbuero-new-session-form"
              variant="primary"
              className="flex-1"
              disabled={createSessionMutation.isLoading}
            >
              {createSessionMutation.isLoading ? (
                <RefreshCw className="mx-auto h-4 w-4 animate-spin" />
              ) : (
                'Erstellen'
              )}
            </Button>
          </div>
        }
      >
            <form id="lernbuero-new-session-form" onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label htmlFor="lb-new-session-teacher" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Lehrer
                </label>
                <input
                  id="lb-new-session-teacher"
                  type="text"
                  value={newSessionForm.Lehrer}
                  onChange={(e) => {
                    const newForm = { ...newSessionForm, Lehrer: e.target.value };
                    setNewSessionForm(newForm);
                    sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify(newForm));
                  }}
                  className="input-field w-full"
                  placeholder="Name des Lehrers"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="lb-new-session-subject" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fach
                </label>
                <select
                  id="lb-new-session-subject"
                  value={newSessionForm.Fach}
                  onChange={(e) => {
                    const newForm = { ...newSessionForm, Fach: e.target.value };
                    setNewSessionForm(newForm);
                    sessionStorage.setItem('lernbuero-new-session-form', JSON.stringify(newForm));
                  }}
                  className="input-field w-full"
                  required
                >
                  <option value="">Fach auswählen</option>
                  {NEW_SESSION_SUBJECTS.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>
            </form>
      </Modal>

      {/* Student Info Modal */}
      {selectedStudentForInfo && selectedStudentForInfo.Unid && (
        <StudentInfoModal
          isOpen={showStudentInfoModal}
          onClose={() => {
            setShowStudentInfoModal(false);
            setSelectedStudentForInfo(null);
          }}
          student={{
            Unid: selectedStudentForInfo.Unid,
            Name: selectedStudentForInfo.Name,
            Vorname: selectedStudentForInfo.Vorname,
            Klasse: selectedStudentForInfo.Klasse,
            Foto: selectedStudentForInfo.Foto,
            sophomorixUnid: selectedStudentForInfo.sophomorixUnid || selectedStudentForInfo.Unid || ''
          }}
        />
      )}

      {/* Memo Modal */}
      {selectedStudentForMemo && (
        <MemoModal
          isOpen={showMemoModal}
          onClose={() => {
            setShowMemoModal(false);
            setSelectedStudentForMemo(null);
          }}
          student={{
            Name: selectedStudentForMemo.Name,
            Vorname: selectedStudentForMemo.Vorname,
            Klasse: selectedStudentForMemo.Klasse,
            Unid: selectedStudentForMemo.Unid,
            Memo: selectedStudentForMemo.Memo
          }}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          to={emailRecipient}
          studentName={emailStudentName}
          initialSubject={emailInitialSubject}
          initialBody={emailInitialBody}
          studentUnid={selectedStudentForInfo?.Unid || selectedStudentForPanelB?.Unid}
          context="lernbuero" // Lernbüro-Kontext für spezifisches Prompt
        />
      )}

      {/* Personal data required modal */}
      <PersonalDataRequiredModal
        isOpen={showPersonalDataModal}
        onClose={() => {
          setShowPersonalDataModal(false)
          setPendingEmailStudent(null)
        }}
        onSaved={async () => {
          setShowPersonalDataModal(false)
          const pending = pendingEmailStudent
          setPendingEmailStudent(null)
          if (pending) {
            // Retry opening the email flow with updated profile
            await handleEmailClick(pending)
          }
        }}
      />

      {/* Email credential modal */}
      <EmailCredentialModal
        isOpen={showEmailCredentialModal}
        onClose={() => {
          setShowEmailCredentialModal(false)
          setPendingEmailStudent(null)
        }}
        credential={null}
        requiredForSending={true}
        onSave={async (credentials) => {
          try {
            const resp = await api.post('/email/credentials', credentials)
            if (resp.data?.success) {
              toast.success('E-Mail-Konto erfolgreich hinzugefügt')
              setShowEmailCredentialModal(false)
              const pending = pendingEmailStudent
              setPendingEmailStudent(null)
              if (pending) {
                await handleEmailClick(pending)
              }
            } else {
              toast.error(resp.data?.error || 'Fehler beim Speichern des E-Mail-Kontos')
            }
          } catch (e: any) {
            toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Fehler beim Speichern des E-Mail-Kontos')
          }
        }}
      />

      <Modal
        isOpen={showGroupTimerModal}
        onClose={() => setShowGroupTimerModal(false)}
        title="Gruppenarbeit starten"
        size="md"
        footer={
          <div className="flex w-full gap-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowGroupTimerModal(false)}>
              Abbrechen
            </Button>
            <Button type="button" variant="primary" className="flex-1" onClick={startGroupWork}>
              Starten
            </Button>
          </div>
        }
      >
        <label htmlFor="lb-group-timer-min" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Dauer (Minuten, 1–90)
        </label>
        <input
          id="lb-group-timer-min"
          type="number"
          min={1}
          max={90}
          value={groupTimerMinutes}
          onChange={(e) => setGroupTimerMinutes(parseInt(e.target.value || '15', 10))}
          className="input-field w-full"
        />
      </Modal>

      <Modal
        isOpen={showGroupsModal}
        onClose={() => setShowGroupsModal(false)}
        title="Gruppen/Extern"
        size="xl"
        panelClassName="max-h-[80vh] overflow-y-auto"
      >
            {groups.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Users className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p>Keine Einträge in Gruppen/Extern</p>
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group) => {
                  const memberNames = attendanceData
                    .filter((s: any) => group.memberIds.includes(s.Unid))
                    .map((s: any) => `${s.Vorname} ${s.Name}`);
                  
                  const deadlineMs = group.deadline_ms ?? new Date(group.deadline).getTime();
                  const remainingTime = getRemainingTime(deadlineMs);
                  
                  return (
                    <div
                      key={group.id}
                      className={`rounded-lg border p-3 ${
                        remainingTime.overdue
                          ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/35'
                          : 'border-gray-200 dark:border-gray-600 dark:bg-gray-900/50'
                      }`}
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${group.color_class.replace('border-', 'bg-')}`} />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Gruppe {group.id}</span>
                        <span className={`text-sm font-bold tabular-nums ${remainingTime.color}`}>{remainingTime.text}</span>
                        {remainingTime.overdue && (
                          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/60 dark:text-red-200">
                            Überfällig
                          </span>
                        )}
                        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{memberNames.join(', ')}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                        <Button
                          type="button"
                          size="xs"
                          variant="primary"
                          className="min-h-0 justify-center px-2 py-1.5 text-xs"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/lernbuero/sessions/${selectedSessionId}/groups/${group.id}/extend`, { minutes: 5 });
                              const updated: Group = res.data?.data;
                              setGroups(prev => prev.map(x => x.id === group.id ? { ...x, deadline: updated.deadline, deadline_ms: updated.deadline_ms } : x));
                              overdueNotifiedRef.current.delete(group.id);
                              await sendToastDismissEvent(group.id, 'extend');
                            } catch (e: any) {
                              toast.error(e?.response?.data?.message || 'Fehler beim Verlängern');
                            }
                          }}
                        >
                          <Timer className="h-3 w-3 shrink-0" /> +5 Min
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="secondary"
                          className="min-h-0 justify-center bg-emerald-600 px-2 py-1.5 text-xs text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                          onClick={async () => {
                            try {
                              await api.post('/lernpartnerschaften/bulk', {
                                student_ids: group.memberIds,
                                gewichtung: 50,
                                beschreibung: 'Günstige Lernbeziehung (Gruppenarbeit)',
                                halbwertszeit_tage: 60
                              });
                              setGroups(prev => prev.filter(x => x.id !== group.id));
                              overdueNotifiedRef.current.delete(group.id);
                              await sendToastDismissEvent(group.id, 'positive');
                              toast.success('Günstige Lernbeziehungen gespeichert');
                            } catch (e: any) {
                              toast.error(e?.response?.data?.message || 'Fehler beim Erstellen positiver Lernbeziehungen');
                            }
                          }}
                        >
                          <ThumbsUp className="h-3 w-3 shrink-0" /> Günstig + Beenden
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="danger"
                          className="min-h-0 justify-center px-2 py-1.5 text-xs"
                          onClick={async () => {
                            try {
                              await api.post('/lernpartnerschaften/bulk', {
                                student_ids: group.memberIds,
                                gewichtung: -50,
                                beschreibung: 'Ungünstige Lernbeziehung (Gruppenarbeit)',
                                halbwertszeit_tage: 60
                              });
                              setGroups(prev => prev.filter(x => x.id !== group.id));
                              overdueNotifiedRef.current.delete(group.id);
                              await sendToastDismissEvent(group.id, 'negative');
                              toast.success('Ungünstige Lernbeziehungen gespeichert');
                            } catch (e: any) {
                              toast.error(e?.response?.data?.message || 'Fehler beim Erstellen negativer Lernbeziehungen');
                            }
                          }}
                        >
                          <ThumbsDown className="h-3 w-3 shrink-0" /> Ungünstig + Beenden
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="secondary"
                          className="min-h-0 justify-center px-2 py-1.5 text-xs"
                          onClick={async () => {
                            try {
                              await api.delete(`/lernbuero/sessions/${selectedSessionId}/groups/${group.id}`);
                              setGroups(prev => prev.filter(x => x.id !== group.id));
                              overdueNotifiedRef.current.delete(group.id);
                              await sendToastDismissEvent(group.id, 'ok');
                            } catch (e: any) {
                              toast.error(e?.response?.data?.message || 'Fehler beim Beenden');
                            }
                          }}
                        >
                          <X className="h-3 w-3 shrink-0" /> Neutral + Beenden
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
      </Modal>

      <Modal
        isOpen={showGroupWorkWarning}
        onClose={() => {
          setShowGroupWorkWarning(false);
          setGroupWorkConflicts([]);
        }}
        title="Warnung: Ungünstige Lernpartnerschaften"
        description="Es bestehen ungünstige Lernpartnerschaften zwischen den ausgewählten Schülern."
        size="lg"
        headerActions={<AlertTriangle className="h-6 w-6 shrink-0 text-amber-500 dark:text-amber-400" aria-hidden />}
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowGroupWorkWarning(false);
                setGroupWorkConflicts([]);
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={() => {
                setShowGroupWorkWarning(false);
                setGroupWorkConflicts([]);
                setShowGroupTimerModal(true);
              }}
            >
              Weiter
            </Button>
          </div>
        }
      >
              <div className="space-y-2">
                {groupWorkConflicts.map((conflict, index) => {
                  const student1Name = groupWorkConflictNameMap[conflict.schueler1_id] || 
                    (conflict.schueler1_vorname && conflict.schueler1_name
                      ? `${conflict.schueler1_vorname} ${conflict.schueler1_name}`
                      : `Schüler ${conflict.schueler1_id}`);
                  
                  const student2Name = groupWorkConflictNameMap[conflict.schueler2_id] || 
                    (conflict.schueler2_vorname && conflict.schueler2_name
                      ? `${conflict.schueler2_vorname} ${conflict.schueler2_name}`
                      : `Schüler ${conflict.schueler2_id}`);
                  
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40"
                    >
                      <ThumbsDown className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-medium">{student1Name}</span> ↔ <span className="font-medium">{student2Name}</span>
                        <span className="ml-2 text-red-600 dark:text-red-400">(Gewichtung: {conflict.gewichtung})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
      </Modal>

      <Modal
        isOpen={showStudentAdditionWarning && Boolean(pendingStudentData)}
        onClose={() => {
          setShowStudentAdditionWarning(false);
          setStudentAdditionConflicts([]);
          setPendingStudentData(null);
          if (studentWarningTimeoutRef.current) {
            window.clearTimeout(studentWarningTimeoutRef.current);
          }
        }}
        title="Warnung: Ungünstige Lernpartnerschaften"
        description={
          pendingStudentData ? (
            <>
              Es bestehen ungünstige Lernpartnerschaften zwischen{' '}
              <strong className="text-gray-900 dark:text-gray-100">
                {pendingStudentData.vorname} {pendingStudentData.name}
              </strong>{' '}
              und bereits anwesenden Schülern.
            </>
          ) : null
        }
        size="lg"
        headerActions={<AlertTriangle className="h-6 w-6 shrink-0 text-amber-500 dark:text-amber-400" aria-hidden />}
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                if (pendingStudentData) {
                  handleRemoveStudentFromWarning(pendingStudentData);
                }
                setShowStudentAdditionWarning(false);
                setStudentAdditionConflicts([]);
                setPendingStudentData(null);
                if (studentWarningTimeoutRef.current) {
                  window.clearTimeout(studentWarningTimeoutRef.current);
                }
              }}
            >
              Wegschicken
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={() => {
                if (pendingStudentData) {
                  createAttendanceToast(pendingStudentData.name, pendingStudentData.vorname, null);
                }
                setShowStudentAdditionWarning(false);
                setStudentAdditionConflicts([]);
                setPendingStudentData(null);
                if (studentWarningTimeoutRef.current) {
                  window.clearTimeout(studentWarningTimeoutRef.current);
                }
              }}
            >
              Aufnehmen
            </Button>
          </div>
        }
      >
              <div className="space-y-2">
                {studentAdditionConflicts.map((conflict, index) => {
                  const student1Name = studentAdditionConflictNameMap[conflict.schueler1_id] || 
                    (conflict.schueler1_vorname && conflict.schueler1_name
                      ? `${conflict.schueler1_vorname} ${conflict.schueler1_name}`
                      : `Schüler ${conflict.schueler1_id}`);
                  
                  const student2Name = studentAdditionConflictNameMap[conflict.schueler2_id] || 
                    (conflict.schueler2_vorname && conflict.schueler2_name
                      ? `${conflict.schueler2_vorname} ${conflict.schueler2_name}`
                      : `Schüler ${conflict.schueler2_id}`);
                  
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40"
                    >
                      <ThumbsDown className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-medium">{student1Name}</span> ↔ <span className="font-medium">{student2Name}</span>
                        <span className="ml-2 text-red-600 dark:text-red-400">(Gewichtung: {conflict.gewichtung})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
      </Modal>

      {/* Überfällig-Dialog entfernt – wird nun per persistentem Toast gehandhabt */}
    </div>
  );
}
